import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ExternalLink } from "lucide-react";

import type { OpsCustomerSummary, OpsPermission } from "../../shared/operations-schemas";
import { AuditExplorer } from "../audit/AuditExplorer";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { FieldError } from "../components/ui/field";
import { CommercialDocumentPage } from "./CommercialDocumentPage";
import { CustomerBilling } from "./CustomerBilling";
import { CustomerDocumentsTab } from "./CustomerDocumentsTab";
import { CustomerHome } from "./CustomerHome";
import { CustomerHubs } from "./CustomerHubs";
import { CustomerPeople } from "./CustomerPeople";
import { customerDetailOptions } from "./customer-queries";
import type { CustomerRoute, CustomerSection } from "./customer-route";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { SupportAccess } from "./SupportAccess";
import { ToolHubPage } from "./ToolHubPage";
import { usePrimaryContact } from "./use-primary-contact";

type SectionRoute = Exclude<CustomerRoute, { section: "document" }>;

const TABS: { key: CustomerSection; label: string; path: string; also: CustomerSection[] }[] = [
  { key: "home", label: "Home", path: "", also: [] },
  { key: "tools", label: "Tools", path: "/tools", also: ["tool"] },
  { key: "documents", label: "Documents", path: "/documents", also: ["notes"] },
  { key: "billing", label: "Billing", path: "/billing", also: [] },
  { key: "people", label: "People", path: "/people", also: [] },
];

/** ADR-0088: five tabs; rarely used pages live under More. */
function CustomerTabs({
  navigate,
  route,
  canAudit,
}: {
  navigate: OperationsNavigate;
  route: SectionRoute;
  canAudit: boolean;
}) {
  const more = [
    { key: "support", label: "Support access" },
    ...(canAudit ? [{ key: "audit", label: "Audit trail" }] : []),
  ];
  const moreActive = more.some((item) => item.key === route.section);
  return (
    <nav
      aria-label="Customer"
      className="flex items-end gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--line)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {TABS.map((tab) => {
        const active = tab.key === route.section || tab.also.includes(route.section);
        return (
          <OperationsLink
            key={tab.key}
            ariaCurrent={active ? "page" : undefined}
            className={`min-h-12 shrink-0 border-b-[3px] px-4 py-3 font-semibold no-underline ${
              active
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            href={`/customers/${route.slug}${tab.path}`}
            navigate={navigate}
          >
            {tab.label}
          </OperationsLink>
        );
      })}
      <DropdownMenu>
        <DropdownMenuTrigger
          className={`inline-flex min-h-12 shrink-0 items-center gap-1 border-b-[3px] px-4 py-3 font-semibold outline-none focus-visible:shadow-[var(--focus-ring)] ${
            moreActive
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          More <ChevronDown className="size-4" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {more.map((item) => (
            <DropdownMenuItem
              key={item.key}
              onClick={() => {
                navigate(`/customers/${route.slug}/${item.key}`);
              }}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}

function SetupNotice({ status }: { status: string }) {
  const failed = status === "failed" || status === "degraded";
  return (
    <div
      role="status"
      className={`flex flex-col gap-2 rounded-xl px-6 py-4 sm:flex-row sm:items-center sm:justify-between ${failed ? "bg-destructive-background" : "bg-info-background"}`}
    >
      <div>
        <p className="font-semibold">
          {failed ? "Customer setup needs attention" : "Customer setup is in progress"}
        </p>
        <p className="text-muted-foreground">
          {failed
            ? "Open Provisioning to review the failed step and retry it."
            : "Contacts, notes, and files unlock once the customer’s private storage is ready."}
        </p>
      </div>
      <Badge variant={failed ? "destructive" : "default"}>
        {failed ? "Needs attention" : "Setting up"}
      </Badge>
    </div>
  );
}

function CustomerHeader({
  customer,
  permissions,
  navigate,
}: {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
}) {
  const ready = customer.provisioningStatus === "ready";
  const { primary } = usePrimaryContact(customer.id, ready && permissions.has("context.read"));
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="font-heading text-[2rem] leading-tight font-extrabold text-balance">
          {customer.name}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {new URL(customer.workspaceUrl).hostname}
          {primary ? (
            <>
              {" · Primary contact "}
              <OperationsLink
                className="font-semibold text-link"
                href={`/customers/${customer.slug}/people`}
                navigate={navigate}
              >
                {primary.displayName}
              </OperationsLink>
            </>
          ) : null}
          {customer.status === "active" ? "" : ` · ${customer.status}`}
        </p>
      </div>
      <a
        className="inline-flex min-h-11 w-fit shrink-0 items-center gap-2 rounded-md bg-muted px-4 font-semibold text-foreground no-underline hover:bg-[color-mix(in_srgb,var(--surface-2)_80%,var(--line))]"
        href={customer.workspaceUrl}
        rel="noreferrer"
        target="_blank"
      >
        Open workspace <ExternalLink className="size-4" />
      </a>
    </header>
  );
}

function SectionIntro({ id, title, children }: { id: string; title: string; children: string }) {
  return (
    <div>
      <h2 id={id} className="text-xl font-bold">
        {title}
      </h2>
      <p className="mt-1 max-w-3xl text-muted-foreground">{children}</p>
    </div>
  );
}

type SectionProps = {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
  route: SectionRoute;
};

function MorePages({ customer, permissions, route }: SectionProps) {
  if (route.section === "audit")
    return permissions.has("audit.read") ? (
      <AuditExplorer embedded audience="operations" organizationId={customer.id} />
    ) : (
      <FieldError>You do not have permission to read this customer’s audit trail.</FieldError>
    );
  return (
    <section aria-labelledby="customer-support-title" className="space-y-5">
      <SectionIntro id="customer-support-title" title="Support access">
        Ask the customer to approve an expiring session, for one tool or the whole workspace. Their
        administrator decides, the approval ends by itself, and they can end it early.
      </SectionIntro>
      <SupportAccess
        organizationId={customer.id}
        canStart={permissions.has("support.session.start")}
        canWrite={permissions.has("support.session.write")}
      />
    </section>
  );
}

function CustomerSectionView(props: SectionProps) {
  const { customer, permissions, navigate, route } = props;
  switch (route.section) {
    case "home":
      return <CustomerHome customer={customer} permissions={permissions} navigate={navigate} />;
    case "tools":
      return (
        <CustomerHubs
          organizationId={customer.id}
          customerSlug={customer.slug}
          customerName={customer.name}
          canRead={permissions.has("context.read")}
          canWrite={permissions.has("context.write")}
          navigate={navigate}
        />
      );
    case "tool":
      return <ToolSection {...props} toolId={route.toolId ?? ""} />;
    case "documents":
    case "notes":
      return (
        <CustomerDocumentsTab
          key={route.section}
          customer={customer}
          permissions={permissions}
          navigate={navigate}
          view={route.section}
          initialFilter={route.filter ?? "all"}
        />
      );
    case "billing":
      return (
        <section aria-labelledby="customer-billing-title" className="flex flex-col gap-6">
          <SectionIntro id="customer-billing-title" title="Billing">
            What we agreed with this customer for each tool, and what has been collected. Only our
            team sees this page.
          </SectionIntro>
          <CustomerBilling customer={customer} permissions={permissions} />
        </section>
      );
    case "people":
      return <CustomerPeople customer={customer} permissions={permissions} navigate={navigate} />;
    default:
      return <MorePages {...props} />;
  }
}

function ToolSection({
  customer,
  permissions,
  navigate,
  toolId,
}: SectionProps & { toolId: string }) {
  return (
    <ToolHubPage
      key={toolId}
      customer={{
        id: customer.id,
        slug: customer.slug,
        name: customer.name,
        ready: customer.provisioningStatus === "ready",
      }}
      hubId={toolId}
      navigate={navigate}
      permissions={{
        canRead: permissions.has("context.read"),
        canWrite: permissions.has("context.write"),
        canUpload: permissions.has("artifact.upload"),
        canSearch: permissions.has("context.search"),
        canReadFiles: permissions.has("artifact.read"),
        canReadBilling: permissions.has("billing.read"),
        canManageBilling: permissions.has("billing.manage"),
        canUsePages: permissions.has("docs.read") && permissions.has("docs.write"),
      }}
    />
  );
}

export function CustomerWorkspace({
  navigate,
  permissions,
  route,
}: {
  navigate: OperationsNavigate;
  permissions: ReadonlySet<OpsPermission>;
  route: CustomerRoute;
}) {
  const customerQuery = useQuery(customerDetailOptions(route.slug));
  if (customerQuery.isPending) {
    return <ContentLoading label="Loading customer…" variant="cards" />;
  }
  if (customerQuery.error) return <FieldError>{customerQuery.error.message}</FieldError>;
  const customer = customerQuery.data?.customer;
  if (!customer) return null;
  if (route.section === "document") {
    return (
      <CommercialDocumentPage
        key={customer.id}
        route={route}
        customerName={customer.name}
        canRead={permissions.has("billing.read")}
        canWrite={permissions.has("billing.manage")}
        navigate={navigate}
      />
    );
  }
  return (
    <div className="customer-workspace space-y-6">
      <OperationsLink
        className="inline-flex min-h-11 items-center gap-2 text-link hover:underline"
        href="/"
        navigate={navigate}
      >
        <ArrowLeft className="size-4" /> All customers
      </OperationsLink>
      <CustomerHeader customer={customer} permissions={permissions} navigate={navigate} />
      {customer.provisioningStatus !== "ready" ? (
        <SetupNotice status={customer.provisioningStatus} />
      ) : null}
      <CustomerTabs navigate={navigate} route={route} canAudit={permissions.has("audit.read")} />
      <CustomerSectionView
        key={customer.id}
        customer={customer}
        navigate={navigate}
        permissions={permissions}
        route={route}
      />
    </div>
  );
}
